/// <reference path="./global.d.ts" />
import { destroyDiContainer, diInject } from '@di/di';
import { ActionOperationMethod, ActionTrigger } from '@domain/action';
import { characterProperties, textProperties } from '@domain/property';
import { InternalWidgetEvent } from '@domain/widget';
import { isAndroid, isIpad, isSafari, preciseIOSVersion } from '@studio/utils/ad/browser';
import { isElementDescendantOfElement } from '@studio/utils/dom-utils';
import { EventEmitter } from '@studio/utils/event-emitter';
import { FontLoader } from '@studio/utils/font-loading';
import { Matrix } from '@studio/utils/matrix';
import { isBase64Image, isUrl } from '@studio/utils/url';
import { deepEqual, getObjectDifference, isNumber, pick } from '@studio/utils/utils';
import { isCancelTrigger, registerActionEventHandlers } from './actions/actions.utils';
import { getCustomFPSInterval, isElementVisibleAtTime, isTimeAt } from './animation.utils';
import { AnimatorLite } from './animator-lite';
import { RichText } from './elements/rich-text/rich-text';
import { isContentSpan } from './elements/rich-text/utils';
import { getFilterString } from './elements/utils';
import { getOriginalUrl, ImageOptimizerUrlBuilder } from './image-optimizer';
import { isCreativeNode, isHidden } from './nodes';
import { createViewElement, forEachDataElement, forEachElement, isGroupDataNode, isImageNode, isImageOrVideoNode, isMaskedNode, isMaskingSupported, isMaskNode, isTextDataElement, isTextNode, isUsedInMask, isVideoNode, isWidgetNode, resetNodeCid, toFlatNodeList } from './nodes/helpers';
import { animationsToState, getTextShadowPropertyValue, getViewElementPropertyValue, maskingAnimationsToState } from './rendering/states.utils';
import { SVGBackground } from './svg-background';
import { visitAllNodes, visitOneNode } from './visitor';
let instanceCounter = 0;
export class Renderer extends EventEmitter {
    get canvasSize_m() {
        return { width: this._viewTree.width, height: this._viewTree.height };
    }
    get _creativeSize() {
        return { width: this.creativeDocument.width, height: this.creativeDocument.height };
    }
    get isEditorMode() {
        return this._creativeConfig.environment === 1 /* AdEnvironment.App */ && !this._isPlaying;
    }
    get creativeDocument() {
        return this._creativeDocument;
    }
    constructor(creativeDocument, _options) {
        super();
        this._options = _options;
        this.feedStore = diInject(6 /* Token.FEED_STORE */, { optional: true });
        this._creativeConfig = diInject(2 /* Token.CREATIVE_CONFIG */);
        this.styleRules_m = new Map();
        this.storedViewElements = new Map();
        this.preloadingElements = new Map();
        this.dynamicImageElementIds = new Set();
        this.time_m = 0;
        this.destroyed_m = false;
        this._muted = true;
        this._htmlElements = new Map();
        this._toggledStatesMap = new Map();
        this._preventNextClickThrough_m = false;
        this._logger = window['logger'] && this._creativeConfig.environment === 1 /* AdEnvironment.App */
            ? new window['logger'](`Renderer${instanceCounter++}`)
            : undefined;
        this._scale = 1;
        this._textScale = 1;
        this._fontFamilies = [];
        this._inStudioApp = !!this._creativeConfig.appView;
        this.updateViewElementValues = () => {
            this.setAllViewElementsValues_m(this.time_m);
        };
        /**
         * Ugly workaround for mouseenter/mouseleave on text elements for actions.
         * Events propagate in nasty ways causing mousenter to get
         * stuck on random occasions so we have to "manually" trigger
         * mouseleave event if the element no longer is being hovered
         */
        this._onCreativeMouseMove = (e) => {
            if (this.isEditorMode) {
                return;
            }
            forEachElement(this._viewTree, element => {
                var _a;
                if (!element.__actionListeners) {
                    return;
                }
                for (const actionListener of element.__actionListeners) {
                    if (actionListener.trigger !== ActionTrigger.MouseLeave) {
                        continue;
                    }
                    const actionStateEnabled = this.getAdditionalStates_m(element.__data).find(additionalState => actionListener.action.operations.find(op => op.value === additionalState.state.id));
                    if (actionStateEnabled &&
                        !((_a = actionStateEnabled === null || actionStateEnabled === void 0 ? void 0 : actionStateEnabled.animation) === null || _a === void 0 ? void 0 : _a.isPlaying) &&
                        !isElementDescendantOfElement(element.__rootElement, e.target)) {
                        actionListener.event(e, true);
                    }
                }
            });
        };
        this._onClick = (event, deepLinkUrl, viewElement) => {
            var _a, _b;
            if (this.isEditorMode) {
                return;
            }
            if (this._preventNextClickThrough_m) {
                event.stopPropagation();
                this._preventNextClickThrough_m = false;
                return;
            }
            /**
             * Check if the element clicked on has any click actions and trigger those.
             * The triggered action will loop back here without a viewElement to
             * continue with the clickthrough
             */
            if (viewElement) {
                const isClickAction = this._triggerClickActions(event, viewElement);
                if (isClickAction) {
                    return;
                }
            }
            if (!this._inStudioApp) {
                event.stopPropagation();
            }
            const x = (_a = event['x']) !== null && _a !== void 0 ? _a : 0;
            const y = (_b = event['y']) !== null && _b !== void 0 ? _b : 0;
            this.emit('click', { event: { x, y }, deepLinkUrl });
        };
        this._fontFamilies = _options.fontFamilies;
        this._setCreativeDocument(creativeDocument);
        this._isEditable = _options.isEditable || false;
        this._window = _options.contentWindow || window;
        this._browserDocument = this._window.document;
        this._styleElement = this._browserDocument.createElement('style');
        this._browserDocument.head.appendChild(this._styleElement);
        this._styleSheet = this._styleElement.sheet;
        this._fragment = this._browserDocument.createDocumentFragment();
        FontLoader.setBrowserDocument(this._browserDocument);
        FontLoader.setEnvs(this._creativeConfig);
        this._resetViewTree();
        this._imageDebounceTimeouts = this._inStudioApp ? new Map() : undefined;
        if (this._inStudioApp && this.feedStore) {
            this.feedStore.on('dataChanged', this.updateViewElementValues);
        }
    }
    getNodeIndex_m(node) {
        const index = toFlatNodeList(this.creativeDocument).findIndex(({ id }) => id === node.id);
        if (index === -1) {
            throw new Error(`Could not find node with id: ${node.id}`);
        }
        return index;
    }
    setScopeId_m(scopeId) {
        this._scopeId = scopeId;
    }
    setPlaying_m(isPlaying) {
        this._isPlaying = isPlaying;
    }
    getViewElementById(id) {
        return this._viewTree.nodes.find(element => element.id === id);
    }
    setScaleBasedOnTextFonts_m(creative) {
        if (!isAndroid) {
            return;
        }
        let minFontSize = Number.MAX_VALUE;
        forEachDataElement(creative, element => {
            if (isTextNode(element) && element.fontSize < minFontSize) {
                minFontSize = element.fontSize;
            }
        });
        if (minFontSize < 8) {
            const scale = Math.ceil(8 / minFontSize) + 1;
            const nearestPowerOfTwoScale = 2 ** Math.ceil(Math.log2(scale));
            this._textScale = nearestPowerOfTwoScale;
        }
        return;
    }
    visitCreative_m(creative) {
        this._scopeId = `b${Renderer._scopeIndex}`;
        this.rootElement = this._browserDocument.createElement('div');
        this.rootElement.setAttribute('data-banner', ''); // TODO: Remove this after image renderer is updated
        this.rootElement.setAttribute('data-creative', '');
        this._htmlElements.set(this._scopeId, this.rootElement);
        this._scaleViewTree(creative);
        this._viewTree.fill = creative.fill;
        this._viewTree.__rootElement = this.rootElement;
        this.insertStyle_m(this._scopeId, {
            width: '100%',
            height: '100%',
            boxSizing: 'border-box',
            position: 'relative',
            overflow: !this._inStudioApp ? 'clip' : 'visible',
            fontFamily: 'Helvetica, Arial, Tahoma, "Microsoft Yahei", 微软雅黑, STXihei, 华文细黑, sans-serif',
            '-webkit-font-smoothing': 'antialiased',
            '-moz-osx-font-smoothing': 'grayscale'
        });
        this.setBackgroundElement_m(this._viewTree);
        this._fragment.appendChild(this.rootElement);
        Renderer._scopeIndex++;
        this.rootElement.addEventListener('mousemove', this._onCreativeMouseMove);
        this.emit('creativeVisited', true);
    }
    visitRectangle_m(rectangle) {
        const viewElement = this._createViewElementFromDataElement(rectangle);
        this._createClickListener(viewElement);
        return viewElement;
    }
    visitEllipse_m(ellipse) {
        const viewElement = this._createViewElementFromDataElement(ellipse);
        this._createClickListener(viewElement);
        return viewElement;
    }
    visitText_m(text) {
        return this.visitTextNode_m(text);
    }
    visitButton_m(button) {
        return this.visitTextNode_m(button);
    }
    visitTextNode_m(text) {
        var _a;
        const viewElement = this._createViewElementFromDataElement(text);
        (_a = viewElement.__rootElement) === null || _a === void 0 ? void 0 : _a.classList.add('text');
        this.insertStyle_m(viewElement.elementCid, this.getDefaultStyle_m(viewElement));
        // avoid text rendering in tests (not in E2E tests)
        if (this._creativeConfig.environment === 3 /* AdEnvironment.Test */ && !this._inStudioApp) {
            return viewElement;
        }
        if (this._inStudioApp) {
            if (text.font) {
                for (const [_, style] of text.characterStyles.entries()) {
                    if (style.font) {
                        this._injectFontFace(style.font);
                    }
                }
                this._injectFontFace(text.font);
            }
        }
        this._renderText(viewElement);
        return viewElement;
    }
    visitImage_m(image) {
        const viewElement = this._createViewElementFromDataElement(image);
        this._createClickListenerWithFeed(image, viewElement);
        return viewElement;
    }
    visitWidget_m(widgetDataElement) {
        var _a, _b;
        const viewElement = this._createViewElementFromDataElement(widgetDataElement);
        const creativeElement = (_a = viewElement.__rootElement) === null || _a === void 0 ? void 0 : _a.parentNode;
        if (creativeElement) {
            if (!creativeElement.querySelector(`#widget-${widgetDataElement.id}`)) {
                (_b = this.WidgetRenderer) === null || _b === void 0 ? void 0 : _b.createWidget(viewElement, widgetDataElement);
            }
        }
        return viewElement;
    }
    visitVideo_m(videoDataElement, skipVideoRenderer) {
        const viewElement = this._createViewElementFromDataElement(videoDataElement);
        if (skipVideoRenderer) {
            return viewElement;
        }
        this._createClickListenerWithFeed(videoDataElement, viewElement);
        viewElement.__videoRenderer = diInject(7 /* Token.VIDEO_RENDERER */, {
            args: [viewElement],
            asNew: true
        });
        return viewElement;
    }
    setBackgroundElement_m(node, overrides) {
        var _a;
        if (!node.__svgBackground) {
            node.__svgBackground = new SVGBackground(this._window, node, this, this._creativeConfig);
        }
        const svg = node.__svgBackground;
        const isImageOrVideo = isImageOrVideoNode(node);
        const promise = svg.render_m(node, overrides);
        if (promise && isImageOrVideo) {
            const id = node.id;
            if (!this.preloadingElements.has(id)) {
                (_a = this._logger) === null || _a === void 0 ? void 0 : _a.verbose('[SvgBackground] adding image to preload:', id);
                this.preloadingElements.set(id, promise);
            }
        }
    }
    setCreativeStyle_m(styleKey, styleProperty) {
        Object.assign(this.creativeDocument, { [styleKey]: styleProperty });
        Object.assign(this._viewTree, { [styleKey]: styleProperty });
        this.setBackgroundElement_m(this._viewTree);
    }
    /**
     * Show a state fully without animating it. Used mainly for preview on canvas
     * @param element
     * @param state
     * @param rate value between 0 and 1
     */
    addAdditionalState_m(element, state, rate) {
        const timeStates = this.getAdditionalStates_m(element);
        let timeState = this._getAdditionalState(element, state);
        if (!timeState) {
            timeState = { state, rate };
            timeStates.push(timeState);
        }
        else {
            timeState.rate = rate;
        }
        this._toggledStatesMap.set(element.id, timeStates);
        return timeState;
    }
    getAdditionalStates_m(element) {
        return this._toggledStatesMap.get(element.id) || [];
    }
    clearAdditionalStates_m() {
        this._toggledStatesMap.forEach((_, elementId) => {
            const element = this._getDataElementById(elementId);
            if (element) {
                this.removeAdditionalStates_m(element);
            }
        });
    }
    /**
     * Set view element value without updating DOM
     * @param element
     * @param time
     */
    setViewElementValues_m(element, time, setStyle = true) {
        if (isHidden(element)) {
            this.hideElement_m(element);
            return;
        }
        this.showElement_m(element);
        const viewElement = this.storedViewElements.get(element.id);
        if (!viewElement) {
            throw new Error(`Could not find view node`);
        }
        this._injectDependencies(element);
        viewElement.duration = element.duration;
        viewElement.time = element.time;
        // Always rerender on loop to make sure images/feeded elements doesn't blink
        const isLoop = time === 0;
        const additionalAnimationStates = this._toggledStatesMap.get(element.id);
        const isVisible = isElementVisibleAtTime(element, this.time_m) &&
            !this._shouldHideOverlapElement(viewElement);
        // If not in view don't do any calculation and just hide the element.
        // We can't run this if setStyle === false since that will mess up initialization
        if (setStyle && !isVisible && !isLoop) {
            viewElement.opacity = 0;
            this._renderStyleOnViewElement(viewElement);
        }
        else {
            const animationState = animationsToState(element, this._creativeSize, time, additionalAnimationStates);
            this._setViewElementValuesWithState(viewElement, element, animationState, setStyle);
        }
    }
    setAllViewElementsValues_m(time, shouldRerender) {
        if (this.destroyed_m) {
            return;
        }
        this.time_m = time;
        if (shouldRerender) {
            this.shouldRerender_m = shouldRerender;
        }
        forEachDataElement(this.creativeDocument, element => {
            if (!isHidden(element)) {
                this.setViewElementValues_m(element, time);
            }
        });
        this.shouldRerender_m = false;
    }
    setActions_m(dataElement) {
        var _a, _b, _c;
        const viewElement = this._viewTree.nodes.find(el => el.id === dataElement.id);
        if (isGroupDataNode(viewElement)) {
            return;
        }
        let ActionHandler = {
            callback: (event, trigger, forceClearStates) => {
                if (this.isEditorMode) {
                    return;
                }
                /**
                 * Treat all following events explicitly for touch
                 * to avoid actions potentially triggering more than once
                 */
                if (trigger === ActionTrigger.TouchStart) {
                    this._isTouchEvent_m = true;
                }
                /**
                 * Special check to properly trigger mouseleave
                 * events for text elements
                 */
                if (trigger === ActionTrigger.MouseLeave &&
                    !forceClearStates &&
                    event.target !== viewElement.__rootElement) {
                    return;
                }
                if (this._isTouchEvent_m && trigger.startsWith('mouse')) {
                    return;
                }
                const target = event instanceof MouseEvent ? event.relatedTarget : event.target;
                if (trigger !== ActionTrigger.MouseLeave &&
                    target &&
                    !this._browserDocument.body.contains(target)) {
                    return;
                }
                dataElement.actions
                    .filter(action => action.triggers.find(tr => tr === trigger))
                    .forEach(action => {
                    if (action.disabled) {
                        return;
                    }
                    action.operations.forEach(operation => {
                        switch (operation.method) {
                            case ActionOperationMethod.OpenUrl:
                                this._onClick(event, operation.value);
                                break;
                            case ActionOperationMethod.SetState:
                            case ActionOperationMethod.RemoveState:
                            case ActionOperationMethod.ClearStates:
                                if (action.preventClickThrough) {
                                    this._preventNextClickThrough_m = true;
                                }
                                this._setActionState(operation, action, trigger);
                                break;
                        }
                    });
                });
            }
        };
        ActionHandler = Object.assign(Object.assign({}, ActionHandler), registerActionEventHandlers(ActionHandler));
        viewElement.__actionListeners = viewElement.__actionListeners || [];
        for (const listener of viewElement.__actionListeners) {
            // useCapture should be true, since the addEventListener set it to true
            (_a = viewElement.__rootElement) === null || _a === void 0 ? void 0 : _a.removeEventListener(listener.trigger, listener.event, true);
        }
        viewElement.__actionListeners = [];
        for (const action of dataElement.actions) {
            if (action.disabled) {
                continue;
            }
            for (const trigger of action.triggers) {
                /**
                 * Clicks has to be handled from onClick
                 * to avoid event emission race-conditions
                 */
                if (trigger !== ActionTrigger.Click) {
                    if (trigger === ActionTrigger.TouchStart) {
                        (_b = viewElement.__rootElement) === null || _b === void 0 ? void 0 : _b.addEventListener(trigger, ActionHandler[trigger], {
                            passive: true
                        });
                    }
                    else {
                        (_c = viewElement.__rootElement) === null || _c === void 0 ? void 0 : _c.addEventListener(trigger, ActionHandler[trigger], true);
                    }
                }
                viewElement.__actionListeners.push({
                    trigger,
                    event: ActionHandler[trigger],
                    action
                });
            }
        }
    }
    rerender_m(creative) {
        var _a;
        (_a = this._logger) === null || _a === void 0 ? void 0 : _a.debug(`rerender_m`);
        this._onlyRenderTextWithoutFontLoad = false;
        this._setCreativeDocument(creative);
        this._clearElementHandlers();
        this._resetViewTree();
        this._fragment = this._browserDocument.createDocumentFragment();
        resetNodeCid();
        this._mountPoint.innerHTML = '';
        this._removeStyles();
        this._htmlElements.clear();
        visitAllNodes(this.creativeDocument, this);
        this._renderMasking();
        this.rootElement.addEventListener('click', this._onClick);
        this._setBaseStyles(this._onlyRenderTextWithoutFontLoad);
        this.updateElementOrder_m();
        this._mountPoint.appendChild(this._fragment);
    }
    initialize(mountPoint, time = 0, onlyRenderTextWithoutFontLoad = false) {
        var _a;
        (_a = this._logger) === null || _a === void 0 ? void 0 : _a.verbose('initialize');
        if (this.destroyed_m) {
            return;
        }
        // set host element
        this._mountPoint = mountPoint;
        // reset to initial state
        this._resetInitialState(time, onlyRenderTextWithoutFontLoad, false);
        // determine & set scale
        const scale = this._getContentScale(mountPoint.getBoundingClientRect());
        this._setScale(scale);
        if (isAndroid) {
            this.setScaleBasedOnTextFonts_m(this.creativeDocument);
        }
        for (const node of this.creativeDocument.elements) {
            this._injectDependencies(node);
        }
        // visit creative and all elements
        visitAllNodes(this.creativeDocument, this, onlyRenderTextWithoutFontLoad);
        // insert base styles
        this._setBaseStyles(onlyRenderTextWithoutFontLoad);
        // render masking
        this._renderMasking();
        // append creative container to host
        this._mountPoint.appendChild(this._fragment);
        // add observers
        this.rootElement.addEventListener('click', this._onClick);
        // should be done once the render is fully done, unfortunately the animated creative does magic with feeds and not fully inits all elements
        this._initResizeObserver(mountPoint);
    }
    destroyElement_m(node) {
        const documentNode = this.creativeDocument.findNodeById_m(node.id, true);
        if (!documentNode) {
            throw new Error(`Could not find ${node.kind}`);
        }
        (documentNode.__parentNode || this.creativeDocument).removeNodeById_m(documentNode.id);
        if (isGroupDataNode(documentNode)) {
            return;
        }
        this._destroyViewElement(documentNode);
    }
    destroy() {
        var _a, _b;
        this.emit('destroy');
        if ((_a = this._styleElement) === null || _a === void 0 ? void 0 : _a.parentNode) {
            this._styleElement.parentNode.removeChild(this._styleElement);
        }
        this._clearElementHandlers();
        this.clearAdditionalStates_m();
        if (this.feedStore) {
            this.feedStore.destroy();
            this.feedStore.off('dataChanged', this.updateViewElementValues);
        }
        for (const element of this.creativeDocument.elements) {
            this.destroyElement_m(element);
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this._styleSheet = undefined;
        this.styleRules_m.clear();
        if (this.WidgetRenderer) {
            this.WidgetRenderer.destroy();
        }
        this.storedViewElements.clear();
        this._htmlElements.clear();
        (_b = this._resizeObserver) === null || _b === void 0 ? void 0 : _b.disconnect();
        this.destroyed_m = true;
        destroyDiContainer(this.creativeDocument.id);
        this.clearEvents();
    }
    /**
     * Update order of view elements and DOM based on the order of the dataElements
     */
    updateElementOrder_m() {
        const elements = this.creativeDocument.elements;
        const viewElements = this._viewTree.nodes;
        // Sort view elements in the same order as the data elements.
        viewElements.sort((a, b) => elements.indexOf(a.__data) - elements.indexOf(b.__data));
        // Update indexes on DOM by changing order of elements
        forEachElement(this._viewTree, (element, _group, index) => this._moveViewElementInDom(element, index));
    }
    /**
     * Used to bring a text to top while editing text.
     */
    bringElementToTop_m(element) {
        this._moveViewElementInDom(element, Number.MAX_VALUE);
    }
    getDefaultStyle_m(element) {
        const elementIsVisibleAtTime = isElementVisibleAtTime(element.__data, this.time_m);
        const isMaskedElement = isMaskedNode(element.__data);
        /**
         * Show and hide elements with opacity and pointer-events instead of
         * relying in visiblity due to a bug in widgets on iOS 14
         */
        const opacity = isNumber(element.opacity)
            ? { opacity: `${elementIsVisibleAtTime ? element.opacity : 0}` }
            : undefined;
        const pointerEvents = isMaskedElement || !elementIsVisibleAtTime ? 'none' : 'auto';
        const radius = isNumber(element.radius) ? { borderRadius: `${element.radius}px` } : undefined;
        const filterString = getFilterString(element.filters);
        // See comment about opacity above
        let isIOS = false;
        if (!isIpad && preciseIOSVersion.simple <= 0) {
            isIOS = true;
        }
        const defaultStyles = Object.assign(Object.assign(Object.assign(Object.assign({ pointerEvents }, this.getPositionAndSizeStyle_m(element)), radius), opacity), { visibility: isIOS || elementIsVisibleAtTime ? 'visible' : 'hidden', filter: filterString || 'unset' });
        return defaultStyles;
    }
    rerenderText_m() {
        var _a;
        for (const element of this._viewTree.nodes) {
            if (isTextNode(element)) {
                (_a = element.__richTextRenderer) === null || _a === void 0 ? void 0 : _a.rerender();
            }
        }
    }
    getPositionAndSizeStyle_m(element) {
        return {
            width: `${element.width}px`,
            height: `${element.height}px`,
            transform: this.getTransformMatrix_m(element)
        };
    }
    getTransformString_m(element, excludeTranslate = false) {
        const scale = this._getScaleOfElement(element);
        let transformString = excludeTranslate ? '' : `translate(${element.x}px, ${element.y}px)`;
        if (element.rotationZ) {
            transformString += ` rotateZ(${element.rotationZ}rad)`;
        }
        if (element.rotationY) {
            transformString += ` rotateY(${element.rotationY}rad)`;
        }
        if (element.rotationX) {
            transformString += ` rotateX(${element.rotationX}rad)`;
        }
        transformString += ` scaleX(${scale.x})`;
        transformString += ` scaleY(${scale.y})`;
        return transformString;
    }
    /**
     * Get a css transform matrix3d from view element.
     * @param element
     * @returns
     */
    getTransformMatrix_m(element) {
        const dataNode = element.__data;
        const isMasked = isMaskedNode(dataNode);
        const scale = this._getScaleOfElement(element);
        const matrix = new Matrix();
        matrix.translate_m(element.x, element.y);
        // Set perspective AFTER moving but BEFORE rotating element
        // to always keep perspective-origin in the center of the element
        matrix.perspective_m(element.perspective || 800);
        // Order of these is crucial to 3D-rotate in an expected way
        if (!isMasked) {
            if (element.rotationZ) {
                matrix.rotateZ_m(element.rotationZ);
            }
            if (element.rotationY) {
                matrix.rotateY_m(element.rotationY);
            }
            if (element.rotationX) {
                matrix.rotateX_m(element.rotationX);
            }
            matrix.scale_m(scale.x, scale.y);
            // Hack for blurry images when not "3D" in responsive ads and zoomed canvas.
            // Safari messes up z-index for non 3d rotated elements
            if (!element.rotationY && !element.rotationX && isImageNode(element)) {
                matrix.rotateY_m(Math.PI * 2);
            }
        }
        return matrix.toCSS_m();
    }
    insertStyle_m(cid, style) {
        let selector;
        if (cid !== this._scopeId) {
            selector = `#${this._scopeId} #${cid}`;
        }
        else {
            selector = `#${cid}`;
        }
        let rules = '';
        for (const rule in style) {
            if (style[rule]) {
                const formattedRule = rule.replace(/([A-Z])/g, m => `-${m.toLowerCase()}`);
                rules += `${formattedRule}:${style[rule]};`;
            }
        }
        // Insert rules for element div
        this._styleSheet.insertRule(`${selector}{${rules}}`, this._styleSheet.cssRules.length);
        const styleRule = this._styleSheet.cssRules[this._styleSheet.cssRules.length - 1];
        const styles = this._getStyleValues(styleRule);
        this.styleRules_m.set(cid, {
            previous: {
                styleRule: styleRule,
                rawStyleValue: styles
            },
            current: {
                styleRule: styleRule,
                rawStyleValue: styles
            }
        });
        const element = this._htmlElements.get(cid);
        if (element) {
            element.setAttribute('id', cid);
        }
    }
    getScale_m() {
        return this._scale;
    }
    hideElement_m(element) {
        if (isTextDataElement(element)) {
            this.updateCharacterStyles_m(element);
        }
        this._destroyViewElement(element);
        if (isUsedInMask(element)) {
            if (isMaskNode(element)) {
                SVGBackground._maskingRenderMap.set_m(element, true);
            }
            this.shouldRerender_m = true;
            this._updateMaskedElements(element);
            this.shouldRerender_m = false;
        }
    }
    updateCharacterStyles_m(element, persistDirtyContent = false, skipStylePromotion = false) {
        var _a, _b;
        const viewElement = this.getViewElementById(element.id);
        if (!viewElement) {
            return;
        }
        (_b = (_a = viewElement.__richTextRenderer) === null || _a === void 0 ? void 0 : _a.editor_m) === null || _b === void 0 ? void 0 : _b.resolveCharacterStyles(skipStylePromotion);
        for (const property of characterProperties) {
            if (property in element.content.style) {
                if (property === 'font') {
                    element.font = element.content.style[property];
                    continue;
                }
                element[property] = element.content.style[property];
            }
        }
        if (!persistDirtyContent) {
            element.__dirtyContent = undefined;
        }
    }
    showElement_m(element) {
        const viewElement = this.storedViewElements.get(element.id);
        if (viewElement) {
            return;
        }
        visitOneNode(element, this);
    }
    mute(muted) {
        this._muted = muted !== null && muted !== void 0 ? muted : !this._muted;
        this.emit('mute', this._muted);
    }
    removeAdditionalStates_m(element, renderElement = true) {
        const states = this.getAdditionalStates_m(element);
        this._toggledStatesMap.delete(element.id);
        if (states.length) {
            states.forEach(state => { var _a; return (_a = state.animation) === null || _a === void 0 ? void 0 : _a.pause(); });
            if (renderElement) {
                this.setViewElementValues_m(element, this.time_m);
            }
        }
    }
    removeAdditionalState_m(element, state) {
        const stateMap = this._toggledStatesMap.get(element.id);
        const activeStates = (stateMap === null || stateMap === void 0 ? void 0 : stateMap.filter(s => s.state.id !== state.id)) || [];
        this._toggledStatesMap.delete(element.id);
        this._toggledStatesMap.set(element.id, activeStates);
    }
    _setCreativeDocument(creativeDocument) {
        this._creativeDocument = creativeDocument;
    }
    _createClickListenerWithFeed(element, viewElement) {
        var _a;
        const onClick = (event) => {
            let deepLinkUrl = '';
            if (element.feed && this.feedStore) {
                deepLinkUrl = this.feedStore.getFeedValue(element.feed, element.id).targetUrl;
            }
            this._onClick(event, deepLinkUrl, viewElement);
        };
        this.on('destroy', () => {
            var _a;
            (_a = viewElement.__rootElement) === null || _a === void 0 ? void 0 : _a.removeEventListener('click', onClick);
        });
        (_a = viewElement.__rootElement) === null || _a === void 0 ? void 0 : _a.addEventListener('click', onClick);
    }
    _createClickListener(viewElement) {
        var _a;
        const onElementClick = (event) => {
            if (this.isEditorMode) {
                return;
            }
            this._onClick(event, undefined, viewElement);
        };
        (_a = viewElement.__rootElement) === null || _a === void 0 ? void 0 : _a.addEventListener('click', onElementClick);
        this.on('destroy', () => { var _a; return (_a = viewElement.__rootElement) === null || _a === void 0 ? void 0 : _a.removeEventListener('click', onElementClick); });
    }
    _triggerClickActions(event, viewElement) {
        var _a;
        const actionListeners = ((_a = viewElement.__actionListeners) === null || _a === void 0 ? void 0 : _a.filter(al => al.trigger === ActionTrigger.Click && !al.action.disabled)) || [];
        if (actionListeners.length) {
            for (const listener of actionListeners) {
                if (listener.action.operations.some(op => op.method === ActionOperationMethod.OpenUrl)) {
                    listener.event(event);
                    break;
                }
                listener.event(event);
            }
            return true;
        }
        else {
            return false;
        }
    }
    _getImageUrl(element) {
        var _a;
        const feed = element.feed;
        const viewElement = this.storedViewElements.get(element.id);
        const feedUrl = (_a = this.feedStore) === null || _a === void 0 ? void 0 : _a.getFeedValueUrl(feed, element.id, element);
        const imageAsset = element.imageAsset;
        const inDV = this._creativeConfig.appView === "dv" /* AppView.DesignView */ && this.isEditorMode;
        const inMV = this._creativeConfig.appView === "mv" /* AppView.ManageView */;
        const id = element.id;
        const url = feedUrl || (imageAsset === null || imageAsset === void 0 ? void 0 : imageAsset.url);
        // DV contains base64 images while upload is ongoing, can't be optimized
        if (isBase64Image(url)) {
            return url;
        }
        // Note that the base64 check must be BEFORE this to not cause performance issues with regex
        if (!isUrl(url)) {
            return '';
        }
        // This is a feeded image but no feed value is available this "loop"
        if (feed && !feedUrl) {
            return '';
        }
        // Debounce optimization in DV if a URL already is set
        if ((inDV || inMV) && id && this._imageDebounceTimeouts) {
            const timeout = this._imageDebounceTimeouts.get(id);
            // Clear old timeout to achieve "debounce"
            if (timeout) {
                window.clearTimeout(timeout);
            }
            // Only debounce if URL haven't changed (hence size change)
            if (getOriginalUrl(viewElement.imageUrl) === url) {
                this._imageDebounceTimeouts.set(id, window.setTimeout(() => {
                    this._debouncedGetImageUrl(element);
                }, 1000));
                // Use current url meanwhile
                return viewElement.imageUrl;
            }
        }
        // Don't calculate new image sizes every tick if not needed since it's expensive
        else if (getOriginalUrl(viewElement.imageUrl) === url) {
            return viewElement.imageUrl;
        }
        const sizeLimit = {
            width: this.creativeDocument.width * this._scale * 2,
            height: this.creativeDocument.height * this._scale * 2
        };
        let cropping;
        // in DV we do load the image uncropped, as is
        // Dynamic image elements are excluded from cropping operations because their dimensions or content
        // may change frequently, making cropping impractical or unnecessary.
        const isDynamic = this.dynamicImageElementIds.has(element.id);
        if (!inDV && !isDynamic) {
            cropping = ImageOptimizerUrlBuilder.getCroppingOptions(element);
        }
        return ImageOptimizerUrlBuilder.getUrlByElement_m(element, {
            feedUrl,
            force: inDV,
            sizeLimit,
            cropping,
            env: this._creativeConfig.environment
        });
    }
    /**
     * Update url when this function has not been called in X ms
     */
    _debouncedGetImageUrl(element) {
        if (!this.destroyed_m) {
            const viewElement = this.storedViewElements.get(element.id);
            if (!viewElement) {
                return;
            }
            // Remove url to trigger prevent debounce in this._getImageUrl
            viewElement.imageUrl = undefined;
            viewElement.imageUrl = this._getImageUrl(element);
            // Update in DOM
            this.setBackgroundElement_m(viewElement);
        }
    }
    _getAdditionalState(element, state) {
        const timeStates = this.getAdditionalStates_m(element);
        return timeStates.find(s => state === s.state);
    }
    _renderText(textElement) {
        var _a, _b;
        let richTextRenderer = textElement.__richTextRenderer;
        const textDataElement = textElement.__data;
        if (richTextRenderer) {
            richTextRenderer.rerender();
            return;
        }
        const options = {
            feedStore: this.feedStore,
            element: textDataElement,
            viewElement: textElement,
            skipFontLoad: this._onlyRenderTextWithoutFontLoad,
            document: this.creativeDocument,
            isEditable: this._isEditable,
            fontBoostingScale: this._textScale
        };
        const text = {
            spans: ((_a = textDataElement.__dirtyContent) === null || _a === void 0 ? void 0 : _a.spans) || textDataElement.content.spans,
            style: pick(Object.assign(textElement, ((_b = textDataElement.__dirtyContent) === null || _b === void 0 ? void 0 : _b.style) || {}), ...textProperties)
        };
        richTextRenderer = new RichText(text, textElement.__rootElement, options, this._fontFamilies);
        textElement.__richTextRenderer = richTextRenderer;
        richTextRenderer.init_m();
        const onElementInteractionsStarted = () => {
            this.emit('textElementInteractionsStarted', richTextRenderer);
        };
        const onElementInteractionsEnded = () => {
            this.emit('textElementInteractionsEnded', richTextRenderer);
        };
        const onElementDestroyed = () => {
            richTextRenderer === null || richTextRenderer === void 0 ? void 0 : richTextRenderer.off('interactionsStarted', onElementInteractionsStarted);
            richTextRenderer === null || richTextRenderer === void 0 ? void 0 : richTextRenderer.off('interactionsEnded', onElementInteractionsEnded);
            richTextRenderer === null || richTextRenderer === void 0 ? void 0 : richTextRenderer.off('destroyed', onElementDestroyed);
        };
        const onSpanClicked = ({ event, deepLinkUrl }) => {
            this._onClick(event, deepLinkUrl);
        };
        const onTextClick = (event) => {
            var _a;
            let deepLinkUrl = '';
            try {
                if (this.feedStore) {
                    const spans = ((_a = textElement.__richTextRenderer) === null || _a === void 0 ? void 0 : _a.text_m.spans) || [];
                    for (const span of spans) {
                        if (isContentSpan(span) &&
                            span.type === "variable" /* SpanType.Variable */ &&
                            span.style.variable) {
                            const feedValue = this.feedStore.getFeedValue(span.style.variable, span.style.variable.spanId);
                            deepLinkUrl = feedValue.targetUrl;
                            break;
                        }
                    }
                }
            }
            catch (_b) {
                /* empty */
            }
            this._onClick(event, deepLinkUrl, textElement);
        };
        if (textElement.__rootElement) {
            textElement.__rootElement.removeEventListener('click', onTextClick);
            textElement.__rootElement.addEventListener('click', onTextClick);
        }
        this.on('destroy', () => {
            var _a;
            (_a = textElement.__rootElement) === null || _a === void 0 ? void 0 : _a.removeEventListener('click', onTextClick);
        });
        // Try to remove listeners, renderText is triggered by undo/redo
        richTextRenderer.off('interactionsStarted', onElementInteractionsStarted);
        richTextRenderer.off('interactionsEnded', onElementInteractionsEnded);
        richTextRenderer.off('destroyed', onElementDestroyed);
        richTextRenderer.off('spanClicked', onSpanClicked);
        richTextRenderer.on('interactionsStarted', onElementInteractionsStarted);
        richTextRenderer.on('interactionsEnded', onElementInteractionsEnded);
        richTextRenderer.on('spanClicked', onSpanClicked);
        richTextRenderer.on('destroyed', onElementDestroyed);
    }
    _setViewElementValuesWithState(viewElement, element, state, setStyle = true) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        (_a = this._logger) === null || _a === void 0 ? void 0 : _a.debug(`_setViewElementValuesWithState[${element.name}]`);
        const isWidget = isWidgetNode(element);
        viewElement.x = getViewElementPropertyValue('x', element, state, this._scale);
        viewElement.y = getViewElementPropertyValue('y', element, state, this._scale);
        viewElement.width = getViewElementPropertyValue('width', element, state, isWidget ? undefined : this._scale);
        viewElement.height = getViewElementPropertyValue('height', element, state, isWidget ? undefined : this._scale);
        viewElement.originX = getViewElementPropertyValue('originX', element, state);
        viewElement.originY = getViewElementPropertyValue('originY', element, state);
        viewElement.scaleX = getViewElementPropertyValue('scaleX', element, state, isWidget ? this._scale : undefined);
        viewElement.scaleY = getViewElementPropertyValue('scaleY', element, state, isWidget ? this._scale : undefined);
        viewElement.rotationX = getViewElementPropertyValue('rotationX', element, state);
        viewElement.rotationY = getViewElementPropertyValue('rotationY', element, state);
        viewElement.rotationZ = getViewElementPropertyValue('rotationZ', element, state);
        viewElement.mirrorX = getViewElementPropertyValue('mirrorX', element, state);
        viewElement.mirrorY = getViewElementPropertyValue('mirrorY', element, state);
        viewElement.border = getViewElementPropertyValue('border', element, state, this._scale);
        viewElement.fill = getViewElementPropertyValue('fill', element, state);
        viewElement.shadows = getViewElementPropertyValue('shadows', element, state, this._scale);
        viewElement.radius = getViewElementPropertyValue('radius', element, state, this._scale);
        viewElement.filters = getViewElementPropertyValue('filters', element, state, this._scale);
        viewElement.perspective = this._getPerspective(element);
        if (isMaskedNode(element)) {
            this._applyOpacityFromMaskNode(viewElement, element);
        }
        else {
            viewElement.opacity = getViewElementPropertyValue('opacity', element, state);
        }
        // Need to adjust x & y to compensate for not scaling width and height
        if (isWidget && this._scale !== 1) {
            viewElement.x -= (viewElement.width * (1 - this._scale)) / 2;
            viewElement.y -= (viewElement.height * (1 - this._scale)) / 2;
        }
        if (isImageNode(viewElement)) {
            const imgElement = element;
            if (imgElement.feed) {
                viewElement.feed = getViewElementPropertyValue('feed', imgElement, state);
                imgElement.imageAsset = undefined;
            }
            else if (imgElement.imageAsset) {
                viewElement.feed = imgElement.feed = undefined;
            }
            // Use this only
            viewElement.imageUrl = this._getImageUrl(viewElement.__data);
        }
        // maps to text prop changes, true if property changed, undefined if not changed
        const textPropertyChangedMap = {};
        if (isTextNode(viewElement) && isTextNode(element)) {
            const previousProps = {};
            const dynamicProperties = ['textColor'];
            for (const prop of dynamicProperties) {
                previousProps[prop] = viewElement[prop];
            }
            // scale all text spans shadows as well - STUDIO-8284
            viewElement.content.spans.forEach(span => {
                if (span.type === "word" /* SpanType.Word */ ||
                    span.type === "composition" /* SpanType.Composition */ ||
                    span.type === "space" /* SpanType.Space */ ||
                    span.type === "variable" /* SpanType.Variable */ ||
                    span.type === "newline" /* SpanType.Newline */) {
                    if (span.style.textShadows) {
                        span.style.textShadows = span.style.textShadows.map(shadow => getTextShadowPropertyValue(shadow, this._scale));
                    }
                }
            });
            viewElement.textColor = getViewElementPropertyValue('textColor', element, state);
            viewElement.textShadows = getViewElementPropertyValue('textShadows', element, state, this._scale);
            viewElement.underline = getViewElementPropertyValue('underline', element, state);
            viewElement.strikethrough = getViewElementPropertyValue('strikethrough', element, state);
            viewElement.uppercase = getViewElementPropertyValue('uppercase', element, state);
            viewElement.textOverflow = getViewElementPropertyValue('textOverflow', element, state);
            viewElement.lineHeight = getViewElementPropertyValue('lineHeight', element, state);
            viewElement.fontSize = getViewElementPropertyValue('fontSize', element, state, this._scale);
            viewElement.maxRows = getViewElementPropertyValue('maxRows', element, state);
            viewElement.padding = getViewElementPropertyValue('padding', element, state, this._scale);
            viewElement.characterSpacing = getViewElementPropertyValue('characterSpacing', element, state);
            viewElement.horizontalAlignment = getViewElementPropertyValue('horizontalAlignment', element, state);
            viewElement.verticalAlignment = getViewElementPropertyValue('verticalAlignment', element, state);
            const dirty = element.__dirtyContent;
            const fontSize = (_b = dirty === null || dirty === void 0 ? void 0 : dirty.style.fontSize) !== null && _b !== void 0 ? _b : viewElement.fontSize;
            const textShadows = (_c = dirty === null || dirty === void 0 ? void 0 : dirty.style.textShadows) !== null && _c !== void 0 ? _c : viewElement.textShadows;
            const padding = (_d = dirty === null || dirty === void 0 ? void 0 : dirty.style.padding) !== null && _d !== void 0 ? _d : viewElement.padding;
            (_e = viewElement.__richTextRenderer) === null || _e === void 0 ? void 0 : _e.setStyle_m('textShadows', textShadows, viewElement.__richTextRenderer.style);
            (_f = viewElement.__richTextRenderer) === null || _f === void 0 ? void 0 : _f.setStyle_m('fontSize', fontSize, viewElement.__richTextRenderer.style);
            (_g = viewElement.__richTextRenderer) === null || _g === void 0 ? void 0 : _g.setStyle_m('padding', padding, viewElement.__richTextRenderer.style);
            for (const prop of dynamicProperties) {
                textPropertyChangedMap[prop] = !deepEqual(viewElement[prop], previousProps[prop]);
            }
        }
        if (isVideoNode(viewElement) && viewElement.__videoRenderer) {
            viewElement.__videoRenderer.updateVideo();
        }
        if (isWidget && isWidgetNode(viewElement)) {
            (_h = element.__widget) === null || _h === void 0 ? void 0 : _h.emit(InternalWidgetEvent.ViewNodeChanged, viewElement);
        }
        if (setStyle) {
            this.setBackgroundElement_m(viewElement);
            this._renderStyleOnViewElement(viewElement, textPropertyChangedMap);
        }
        if (isUsedInMask(element)) {
            this._updateMaskedElements(element);
        }
    }
    _shouldHideOverlapElement(viewElement) {
        const rounding = 0.001;
        const viewEndTime = viewElement.time + viewElement.duration;
        // Only do this if current time is at end of element
        if (isTimeAt(viewEndTime, this.time_m, rounding)) {
            return this.creativeDocument.elements.some(({ time }) => isTimeAt(viewEndTime, time, rounding));
        }
        return false;
    }
    /**
     * Update style on DOM with current viewElement values
     */
    _renderStyleOnViewElement(viewElement, textPropertyChangedMap) {
        var _a, _b, _c, _d, _e;
        const styleRule = this.styleRules_m.get(viewElement.elementCid);
        if (!styleRule) {
            throw new Error(`Could not get stylerule for element. ${this._inStudioApp
                ? 'This is most likely caused by the element no longer existing from an undo/redo.'
                : ''}`);
        }
        const { current: currentStyle, previous: previousStyle } = styleRule;
        currentStyle.rawStyleValue = this.getDefaultStyle_m(viewElement);
        const changedStyles = getObjectDifference(previousStyle.rawStyleValue, currentStyle.rawStyleValue);
        const styleProperties = [
            'pointerEvents',
            'transform',
            'width',
            'height',
            'opacity',
            'filter',
            'visibility'
        ];
        for (const property of styleProperties) {
            if (property in changedStyles) {
                if (viewElement.__rootElement) {
                    viewElement.__rootElement.style[property] = changedStyles[property];
                }
            }
        }
        // We only want to change backface visibility if it's not undefined
        if (changedStyles.backfaceVisibility) {
            previousStyle.styleRule.style.backfaceVisibility = changedStyles.backfaceVisibility;
        }
        if (isTextNode(viewElement)) {
            const sizeChanged = typeof changedStyles.width !== 'undefined' ||
                typeof changedStyles.height !== 'undefined';
            let elementIsFeeded = false;
            const feedStore = (_a = viewElement.__richTextRenderer) === null || _a === void 0 ? void 0 : _a.feedStore_m;
            if (sizeChanged || (this.shouldRerender_m && feedStore)) {
                const dataNode = this._getDataElementById(viewElement.id);
                elementIsFeeded =
                    !!(feedStore === null || feedStore === void 0 ? void 0 : feedStore.elements.get(viewElement.id)) ||
                        !!((_b = viewElement.__richTextRenderer) === null || _b === void 0 ? void 0 : _b.text_m.spans.find(span => span.type === "variable" /* SpanType.Variable */)) ||
                        !!((_c = dataNode.__dirtyContent) === null || _c === void 0 ? void 0 : _c.spans.find(span => span.type === "variable" /* SpanType.Variable */));
            }
            if (textPropertyChangedMap === null || textPropertyChangedMap === void 0 ? void 0 : textPropertyChangedMap.textColor) {
                const textColor = viewElement.textColor;
                (_d = viewElement.__richTextRenderer) === null || _d === void 0 ? void 0 : _d.applyOnlyAnimatedStyleOnElement_m({ textColor });
            }
            if (elementIsFeeded || sizeChanged) {
                (_e = viewElement.__richTextRenderer) === null || _e === void 0 ? void 0 : _e.rerender(false, viewElement);
            }
        }
        previousStyle.rawStyleValue = currentStyle.rawStyleValue;
        previousStyle.styleRule = currentStyle.styleRule;
    }
    _updateCreativeValues() {
        // rescale creative background
        this.setBackgroundElement_m(this._viewTree);
    }
    _setActionState(operation, action, trigger) {
        var _a, _b, _c, _d;
        const stateTargetViewElement = this._viewTree.nodes.find(el => el.id === operation.target);
        if (!stateTargetViewElement) {
            return;
        }
        const stateTargetDataElement = stateTargetViewElement.__data;
        const stateId = operation.value;
        if (!stateId &&
            (operation.method === ActionOperationMethod.SetState ||
                operation.method === ActionOperationMethod.RemoveState)) {
            return;
        }
        /**
         * Only trigger the backup MouseLeave if the MouseDown
         * has happened for pressed states
         **/
        if (action.templateId === 'reserved-pressed' && isCancelTrigger(trigger)) {
            const pressedAction = stateTargetDataElement.actions.find(targetAction => targetAction.templateId === 'reserved-pressed' &&
                targetAction.operations.find(ops => ops.value));
            const currentTimeState = this.getAdditionalStates_m(stateTargetDataElement).find(timeState => timeState.state.id === pressedAction.operations[0].value);
            if (!currentTimeState || currentTimeState.rate === 0) {
                return;
            }
        }
        const targetState = stateTargetDataElement.states.find(state => state.id === stateId);
        const duration = (_b = (_a = operation.animation) === null || _a === void 0 ? void 0 : _a.duration) !== null && _b !== void 0 ? _b : 0.2;
        const timingFunction = (_c = operation.animation) === null || _c === void 0 ? void 0 : _c.timingFunction;
        /**
         * When the default animator is paused we'll have to rely
         * on animatorlite's own tick to trigger the
         * rerenderering of the element
         */
        const animatorLiteCallback = () => {
            if (this._isPlaying) {
                return;
            }
            this.setViewElementValues_m(stateTargetDataElement, this.time_m);
        };
        const customFPSInterval = getCustomFPSInterval(this._options.fps);
        const settings = {
            duration,
            timingFunction,
            customFPSInterval,
            callback: animatorLiteCallback
        };
        // Animate in a state
        if (targetState) {
            const currentTimeState = this.getAdditionalStates_m(stateTargetDataElement).find(additionalState => additionalState.animation && additionalState.state.id === targetState.id);
            // State is already visible (with rate 0-1)
            if (currentTimeState) {
                if (operation.method === ActionOperationMethod.RemoveState) {
                    this.getAdditionalStates_m(stateTargetDataElement)
                        .filter(additionalState => targetState === additionalState.state && additionalState.animation)
                        .forEach(timeState => {
                        if (timeState === null || timeState === void 0 ? void 0 : timeState.animation) {
                            timeState.animation.to({ rate: 0 }, settings);
                        }
                    });
                }
                else {
                    (_d = currentTimeState.animation) === null || _d === void 0 ? void 0 : _d.to({ rate: 1 }, settings);
                }
            }
            // Add and animate in the new state from rate 0
            else if (operation.method === ActionOperationMethod.SetState) {
                const timeState = this.addAdditionalState_m(stateTargetDataElement, targetState, 0);
                timeState.animation = AnimatorLite.to(timeState, { rate: 1 }, settings);
                timeState.animation.onCompleted(() => {
                    if (timeState.rate === 0) {
                        this.removeAdditionalState_m(stateTargetDataElement, targetState);
                    }
                });
            }
        }
        // Remove state
        else if (!targetState || operation.method === ActionOperationMethod.ClearStates) {
            this.getAdditionalStates_m(stateTargetDataElement)
                .filter(state => state.animation)
                .forEach(timeState => {
                if (timeState === null || timeState === void 0 ? void 0 : timeState.animation) {
                    timeState.animation.to({ rate: 0 }, settings);
                }
            });
        }
    }
    _renderMasking() {
        this.creativeDocument.elements.forEach(element => {
            if (isMaskingSupported(element)) {
                this._updateMaskedElements(element);
            }
        });
    }
    _updateMaskedElements(element) {
        const maskedElements = this.creativeDocument.elements.filter(el => { var _a; return isUsedInMask(el) && element.id === ((_a = el.masking) === null || _a === void 0 ? void 0 : _a.elementId); });
        for (const maskedElement of maskedElements) {
            this.setViewElementValues_m(maskedElement, this.time_m);
        }
        if (maskedElements.length && !this.shouldRerender_m) {
            SVGBackground._maskingRenderMap.set_m(element, false);
        }
    }
    _applyOpacityFromMaskNode(viewElement, element) {
        const masking = SVGBackground._maskingRenderMap.get_m(element);
        if (masking) {
            for (const elementId of [...masking.elements.values()]) {
                const maskNode = this.creativeDocument.elements.find(({ id }) => id === elementId);
                if (!maskNode || !isMaskNode(maskNode)) {
                    continue;
                }
                const additionalAnimationStates = this._toggledStatesMap.get(element.id);
                const animationState = maskingAnimationsToState(maskNode, element, this.canvasSize_m, this.time_m, additionalAnimationStates);
                const maskedNodeOpacity = getViewElementPropertyValue('opacity', element, animationState);
                const maskNodeOpacity = getViewElementPropertyValue('opacity', maskNode, animationState);
                viewElement.opacity = maskNodeOpacity * maskedNodeOpacity;
            }
        }
    }
    _resetInitialState(time, onlyRenderTextWithoutFontLoad, resetNodeId = true) {
        // clear host element
        this._mountPoint.innerHTML = '';
        this.time_m = time;
        this._onlyRenderTextWithoutFontLoad = onlyRenderTextWithoutFontLoad;
        if (resetNodeId) {
            resetNodeCid();
        }
        this._resetViewTree();
        this._removeStyles();
    }
    _setBaseStyles(onlyRenderTextWithoutFontLoad) {
        this._insertStyles();
        for (const element of this.creativeDocument.elements) {
            if (isHidden(element)) {
                continue;
            }
            if (onlyRenderTextWithoutFontLoad) {
                if (!isTextNode(element)) {
                    continue;
                }
            }
            const viewElement = this.storedViewElements.get(element.id);
            const styleRule = this.styleRules_m.get(viewElement.elementCid).current.styleRule;
            const willChange = [];
            for (let i = 0; i < styleRule.style.length; i++) {
                if (styleRule.style[i] === 'transform') {
                    if ((this._scale !== 1 && isTextNode(element)) || this._scale === 1) {
                        willChange.push(styleRule.style[i]);
                    }
                }
                if (styleRule.style[i].match(/(opacity|perspectiveOrigin)/)) {
                    willChange.push(styleRule.style[i]);
                }
            }
            styleRule.style.willChange = willChange.join(', ');
        }
        this.updateElementOrder_m();
    }
    _clearElementHandlers() {
        var _a, _b;
        forEachElement(this._viewTree, element => {
            var _a;
            if (isTextNode(element)) {
                (_a = element.__richTextRenderer) === null || _a === void 0 ? void 0 : _a.destroy_m();
            }
            if (isVideoNode(element)) {
                element.__videoRenderer.destroy();
            }
            const styleRule = this.styleRules_m.get(element.elementCid);
            styleRule.current.styleRule.style.willChange = '';
            styleRule.previous.styleRule.style.willChange = '';
            if (element.__rootElement) {
                element.__rootElement.removeEventListener('click', this._onClick);
            }
        });
        (_a = this.rootElement) === null || _a === void 0 ? void 0 : _a.removeEventListener('click', this._onClick);
        (_b = this.rootElement) === null || _b === void 0 ? void 0 : _b.removeEventListener('mousemove', this._onCreativeMouseMove);
    }
    _destroyViewElement(node) {
        var _a, _b, _c;
        this.removeAdditionalStates_m(node, false);
        const viewElement = this.storedViewElements.get(node.id);
        if (!viewElement) {
            return;
        }
        (_a = viewElement.__svgBackground) === null || _a === void 0 ? void 0 : _a.removeMasking_m();
        this._viewTree.nodes = this._viewTree.nodes.filter(n => n.id !== viewElement.id);
        this.storedViewElements.delete(viewElement.id);
        const cid = viewElement.elementCid;
        const styleRule = this.styleRules_m.get(cid);
        this._removeStyle(styleRule.current.styleRule);
        const imageTimeout = (_b = this._imageDebounceTimeouts) === null || _b === void 0 ? void 0 : _b.get(cid);
        if (imageTimeout) {
            window.clearTimeout(imageTimeout);
        }
        if (isTextNode(viewElement) && viewElement.__richTextRenderer) {
            viewElement.__richTextRenderer.destroy_m();
            viewElement.__richTextRenderer = undefined;
        }
        if (isWidgetNode(viewElement)) {
            (_c = viewElement.__data.__widget) === null || _c === void 0 ? void 0 : _c.destroy();
        }
        if (isVideoNode(viewElement)) {
            viewElement.__videoRenderer.destroy();
        }
        const nodeElement = viewElement.__safari3dElement || viewElement.__rootElement;
        if (!nodeElement) {
            throw new Error(`HTML element does not exist on canvas.`);
        }
        this.rootElement.removeChild(nodeElement);
    }
    _createViewElementFromDataElement(dataElement) {
        const viewElement = createViewElement(dataElement);
        this.storedViewElements.set(dataElement.id, viewElement);
        this.setViewElementValues_m(dataElement, this.time_m, false);
        viewElement.__rootElement = this._createElement(viewElement);
        this.insertStyle_m(viewElement.elementCid, Object.assign({}, this.getDefaultStyle_m(viewElement)));
        this._viewTree.nodes.push(viewElement);
        this.setBackgroundElement_m(viewElement);
        this.updateElementOrder_m();
        this.setActions_m(dataElement);
        return viewElement;
    }
    /**
     * Move viewElement in DOM to a certain index.
     */
    _moveViewElementInDom(element, newIndex) {
        const domElement = element.__safari3dElement || element.__rootElement;
        const parent = domElement === null || domElement === void 0 ? void 0 : domElement.parentNode;
        if (domElement && parent) {
            // Exclude SVG background from list of child elements
            const children = Array.from(parent.children).filter(e => e.tagName === 'DIV');
            const currentIndex = (children === null || children === void 0 ? void 0 : children.length) ? children.indexOf(domElement) : -1;
            if (currentIndex > -1 && currentIndex !== newIndex) {
                // null puts the element in the end
                parent.insertBefore(domElement, children[newIndex] || null);
            }
        }
    }
    _getPerspective(element) {
        return Math.max(element.width, element.height) * 2;
    }
    _getScaleOfElement(styles) {
        return {
            x: (isNumber(styles.scaleX) ? styles.scaleX : 1) * (styles.mirrorX ? -1 : 1),
            y: (isNumber(styles.scaleY) ? styles.scaleY : 1) * (styles.mirrorY ? -1 : 1)
        };
    }
    _removeStyle(rule) {
        for (let i = this._styleSheet.cssRules.length - 1; i >= 0; i--) {
            if (this._styleSheet.cssRules[i].selectorText === rule.selectorText) {
                this._styleSheet.deleteRule(i);
                break;
            }
        }
    }
    _removeStyles() {
        this.styleRules_m.clear();
        const count = this._styleSheet.cssRules.length;
        for (let i = count - 1; i > 2; i--) {
            this._styleSheet.deleteRule(i);
        }
    }
    _insertStyles() {
        const styleSheet = this._styleSheet;
        // Safari hack to not overlap element
        if (isSafari) {
            styleSheet.insertRule(`#${this._scopeId} .safari-3d {position:absolute;left:0;top:0;bottom:0;right:0,pointer-events:none; transform: translateZ(0); }`, styleSheet.cssRules.length);
        }
        // Element divs
        styleSheet.insertRule(`#${this._scopeId} .element{position:absolute;left:0;top:0;}`, styleSheet.cssRules.length);
        // Text div inside element divs
        styleSheet.insertRule(`#${this._scopeId} .text>div{position:relative;}`, styleSheet.cssRules.length);
        // Text line baseline reset
        styleSheet.insertRule(`#${this._scopeId} .text .text-line>div::before{display:inline;content: " ‌";}`, styleSheet.cssRules.length);
    }
    _getStyleValues(styleRule) {
        const styles = {};
        for (let i = 0; i < styleRule.style.length; i++) {
            const style = styleRule.style[i];
            styles[style] = styleRule.style[styleRule.style[i]];
        }
        return styles;
    }
    _createElement(element) {
        const htmlElement = this._browserDocument.createElement('div');
        this._htmlElements.set(element.elementCid, htmlElement);
        if (this._creativeConfig.environment === 3 /* AdEnvironment.Test */) {
            if (element.name) {
                htmlElement.setAttribute('data-test-name', element.name);
            }
            htmlElement.setAttribute('data-test-id', `element-kind-${element.kind}`);
        }
        htmlElement.classList.add('element', `kind-${element.kind}`);
        if (!isImageNode(element) && !isTextNode(element) && !isCreativeNode(element)) {
            htmlElement.setAttribute('aria-hidden', 'true');
            htmlElement.setAttribute('role', 'presentation');
        }
        if (isSafari) {
            const wrapper3d = this._browserDocument.createElement('div');
            wrapper3d.classList.add('safari-3d');
            wrapper3d.appendChild(htmlElement);
            element.__safari3dElement = wrapper3d;
            this.rootElement.appendChild(wrapper3d);
        }
        else {
            this.rootElement.appendChild(htmlElement);
        }
        return htmlElement;
    }
    async _injectFontFace(fontStyle) {
        await FontLoader.loadFontFace(fontStyle);
    }
    _getDataElementById(id) {
        const dataElement = this.creativeDocument.elements.find(element => element.id === id);
        if (!dataElement) {
            throw new Error('Tried to get non-existing data element.');
        }
        return dataElement;
    }
    _setScale(scale) {
        var _a, _b;
        if (scale === this._scale) {
            return;
        }
        if (scale === 0) {
            (_a = this._logger) === null || _a === void 0 ? void 0 : _a.verbose('Trying to set scale to 0. Aborting.');
            return;
        }
        (_b = this._logger) === null || _b === void 0 ? void 0 : _b.debug(`setting scale: ${scale}`);
        this._scale = scale;
        this._scaleViewTree(this.creativeDocument);
        // sync the new view tree size with the root element
        this.insertStyle_m(this._scopeId, {
            width: `${this._viewTree.width}px`,
            height: `${this._viewTree.height}px`
        });
    }
    _setScaleAndUpdate(scale) {
        this._setScale(scale);
        // workaround because resize observer will trigger updates too early and we dont have a safer way to check everything was inited yet
        if (this.creativeDocument.elements.every(element => this.storedViewElements.has(element.id) || isHidden(element))) {
            this._updateCreativeValues();
            this.updateViewElementValues();
        }
    }
    _scaleViewTree(size) {
        this._viewTree.width = size.width * this._scale;
        this._viewTree.height = size.height * this._scale;
    }
    _calculateScale(containerSize, contentSize) {
        return containerSize / contentSize;
    }
    _initResizeObserver(host) {
        var _a, _b;
        if (((_a = this._options.responsive) === null || _a === void 0 ? void 0 : _a.enabled) && typeof ResizeObserver !== 'undefined') {
            (_b = this._logger) === null || _b === void 0 ? void 0 : _b.verbose('initializing ResizeObserver');
            this._resizeObserver = new ResizeObserver(observerEntries => {
                var _a, _b;
                (_a = this._logger) === null || _a === void 0 ? void 0 : _a.debug('ResizeObserver: resize observed');
                const entry = observerEntries[0];
                const box = (_b = entry.borderBoxSize) === null || _b === void 0 ? void 0 : _b[0];
                let width = box === null || box === void 0 ? void 0 : box.inlineSize; // Safari returns inlineSize undefined
                let height = box === null || box === void 0 ? void 0 : box.blockSize;
                if (!isNumber(width) || !isNumber(height)) {
                    const boundingRect = entry.target.getBoundingClientRect();
                    width = boundingRect.width;
                    height = boundingRect.height;
                }
                const scale = this._getContentScale({ width, height });
                this._setScaleAndUpdate(scale);
            });
            this._resizeObserver.observe(host);
        }
    }
    _resetViewTree() {
        this._viewTree = {
            kind: "creative" /* CreativeKind.Creative */,
            nodes: [],
            width: this.creativeDocument.width,
            height: this.creativeDocument.height
        };
    }
    _getContentScale(containerSize) {
        var _a;
        const isResponsiveContain = ((_a = this._options.responsive) === null || _a === void 0 ? void 0 : _a.mode) === 'contain';
        const containerAspectRatio = containerSize.width / containerSize.height;
        const contentAspectRatio = this.creativeDocument.width / this.creativeDocument.height;
        // If container is wider than content, scale based on height
        if (isResponsiveContain && containerAspectRatio > contentAspectRatio) {
            return this._calculateScale(containerSize.height, this.creativeDocument.height);
        }
        // Otherwise, scale based on width
        return this._calculateScale(containerSize.width, this.creativeDocument.width);
    }
    _injectDependencies(node) {
        if (isWidgetNode(node) && !this.WidgetRenderer) {
            this.WidgetRenderer = diInject(4 /* Token.WIDGET_RENDERER */);
        }
    }
}
Renderer._scopeIndex = 0;
